/**
 * Who the schedule has been kind to. Each week a manager's score is also
 * compared with everyone else's: the share of the other teams it beats is
 * that week's chance of a win, and the chances add up to the wins their
 * scores should have earned. Luck is the wins they actually won minus that.
 *
 * For a season, or all of them added up. Playoff and consolation games are
 * left out, and so are weeks a manager didn't play. A week still in progress
 * (only some of its games final) is left out entirely, actual wins and all:
 * judged against only the teams that have finished, a score would look far
 * luckier or unluckier than it will once the rest are in.
 */

import type { LeagueHistory } from "../history";
import type { PersonGame, Record3 } from "../reportBuilder/ledger";
import { round2 } from "../util/math";

/** A season, or "all" to add up every season. */
export type LuckScope = number | "all";

export interface LuckRow {
  personId: string;
  /** Regular-season games played. */
  games: number;
  actual: Record3;
  /** Wins their scores would be expected to earn: each week's share of the league they beat (a tie counts half), added up. */
  expectedWins: number;
  /** Actual wins (a tie is half) minus expected wins; positive means the schedule helped. */
  luck: number;
}

export interface Luck {
  scope: LuckScope;
  /** Distinct regular-season weeks in scope (per season, added up for "all"). */
  weeks: number;
  /** Luckiest first, then by id. */
  rows: LuckRow[];
}

/**
 * The weeks (as "season|week") that still have a game Yahoo lists as unfinished, which
 * `buildLuck` leaves out. Only the live season's current week can be one; going by the
 * games still to play, not by how many scores a week holds, keeps a bye or an odd-sized
 * league from being mistaken for a week in progress.
 */
export function weeksInProgress(history: LeagueHistory): ReadonlySet<string> {
  const keys = history.seasons.flatMap((s) => s.upcoming.filter((g) => g.gameType === "regular").map((g) => `${s.season}|${g.week}`));
  return new Set(keys);
}

/** `inProgress` is the weeks still being played (see `weeksInProgress`); without it every finished game counts. */
export function buildLuck(ledger: readonly PersonGame[], scope: LuckScope, inProgress: ReadonlySet<string> = new Set()): Luck {
  const games = ledger.filter((g) => g.gameType === "regular" && (scope === "all" || g.season === scope) && !inProgress.has(`${g.season}|${g.week}`));

  const byWeek = new Map<string, PersonGame[]>();
  for (const g of games) {
    const key = `${g.season}|${g.week}`;
    byWeek.set(key, [...(byWeek.get(key) ?? []), g]);
  }

  const totals = new Map<string, { games: number; actual: Record3; expectedWins: number }>();
  for (const g of games) {
    const t = totals.get(g.personId) ?? { games: 0, actual: { wins: 0, losses: 0, ties: 0 }, expectedWins: 0 };
    t.games++;
    if (g.result === "W") t.actual.wins++;
    else if (g.result === "L") t.actual.losses++;
    else t.actual.ties++;
    totals.set(g.personId, t);
  }

  // A week's chance of a win is the share of the other teams that week the score beats.
  for (const week of byWeek.values()) {
    if (week.length < 2) continue;
    for (const mine of week) {
      let beaten = 0;
      for (const theirs of week) {
        if (theirs !== mine) beaten += mine.points > theirs.points ? 1 : mine.points === theirs.points ? 0.5 : 0;
      }
      totals.get(mine.personId)!.expectedWins += beaten / (week.length - 1);
    }
  }

  const rows = [...totals.entries()]
    .map(([personId, t]) => ({
      personId,
      games: t.games,
      actual: t.actual,
      expectedWins: round2(t.expectedWins),
      luck: round2(t.actual.wins + t.actual.ties / 2 - t.expectedWins),
    }))
    .sort((a, b) => b.luck - a.luck || a.personId.localeCompare(b.personId));

  return { scope, weeks: byWeek.size, rows };
}
